import React from 'react';
// import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link,
  IconButton,
  Chip,
  Grid,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';

interface Finding {
  id: string;
  fileName: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  context: string;
}

// Mock data for a single repository
const mockRepoData = {
  name: 'E-Commerce Frontend',
  repoLink: 'https://github.com/org/frontend-app',
  healthScore: 85,
  secrets: {
    critical: 1,
    high: 2,
    medium: 3,
    low: 5,
  },
  findings: [
    {
      id: '1',
      fileName: 'config.js',
      severity: 'CRITICAL',
      context: 'AWS access key found in configuration file',
    },
    {
      id: '2',
      fileName: 'database.ts',
      severity: 'HIGH',
      context: 'Database credentials in plain text',
    },
    {
      id: '3',
      fileName: 'auth.js',
      severity: 'MEDIUM',
      context: 'OAuth client secret exposed',
    },
  ] as Finding[],
};

const SeverityChip = ({ severity }: { severity: string }) => {
  const getColor = () => {
    switch (severity) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'MEDIUM':
        return 'info';
      case 'LOW':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={severity}
      color={getColor()}
      size="small"
    />
  );
};

const HealthScoreCircle = ({ score }: { score: number }) => {
  const getGrade = (score: number) => {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  };

  return (
    <Box
      sx={{
        width: 100,
        height: 100,
        borderRadius: '50%',
        bgcolor: 'success.main',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
      }}
    >
      <Typography variant="h3">{getGrade(score)}</Typography>
      <Typography variant="caption">{score}/100</Typography>
    </Box>
  );
};

const RepositoryDetails: React.FC = () => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Repository Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Repository Summary
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Source/Repo Link
              </Typography>
              <Link href={mockRepoData.repoLink} target="_blank">
                {mockRepoData.repoLink}
              </Link>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Project Name
              </Typography>
              <Typography>{mockRepoData.name}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Secrets by Severity
              </Typography>
              <Box display="flex" gap={1}>
                <Chip label={`Critical: ${mockRepoData.secrets.critical}`} color="error" size="small" />
                <Chip label={`High: ${mockRepoData.secrets.high}`} color="warning" size="small" />
                <Chip label={`Medium: ${mockRepoData.secrets.medium}`} color="info" size="small" />
                <Chip label={`Low: ${mockRepoData.secrets.low}`} color="success" size="small" />
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
            <Box textAlign="center">
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Health Score
              </Typography>
              <HealthScoreCircle score={mockRepoData.healthScore} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Detailed Findings */}
      <Typography variant="h5" gutterBottom>
        Detailed Findings
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Context</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mockRepoData.findings
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((finding) => (
                <TableRow key={finding.id}>
                  <TableCell>{finding.fileName}</TableCell>
                  <TableCell>
                    <SeverityChip severity={finding.severity} />
                  </TableCell>
                  <TableCell>{finding.context}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" title="More Information">
                      <InfoIcon />
                    </IconButton>
                    <IconButton size="small" title="Fix">
                      <BuildIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={mockRepoData.findings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default RepositoryDetails;
